import React, { useEffect, useState } from "react";
import { Bell, KeyRound, Laptop, LogOut, Mail, Save, ShieldAlert, ShieldCheck, Smartphone, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Badge } from "./ui/badge";
import { AvatarEditor } from "./AvatarEditor";
import { api, errMsg } from "../lib/api";
import { useSuperSecurity } from "./TwoStepBanner";
import { getSuperDeviceToken, setSuperDeviceToken } from "./SuperOtpStep";

const EVENT_LABEL = {
  email_changed: "Sign-in email changed", password_changed: "Password changed",
  mobile_changed: "Mobile number changed", new_device_login: "New sign-in",
};
const fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};

/** My account → Security: two-step status, trusted browsers, alert channels and recent security events. */
function SecurityTab({ open, profile }) {
  const { data, loading, reload } = useSuperSecurity(open);
  const [busyId, setBusyId] = useState(null);
  const ts = data?.two_step;
  const devices = data?.trusted_devices || [];
  const myToken = getSuperDeviceToken();

  const revoke = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/platform/auth/trusted-devices/${id}`);
      if (devices.find((d) => d.id === id)?.current) setSuperDeviceToken(null);
      toast.success("Browser signed out — it will need an SMS code next time");
      reload();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusyId(null); }
  };
  const revokeOthers = async () => {
    setBusyId("all");
    try {
      const r = await api.delete("/platform/auth/trusted-devices", { params: { keep_device_token: myToken || undefined } });
      toast.success(r.data.revoked ? `${r.data.revoked} other browser${r.data.revoked === 1 ? "" : "s"} signed out` : "No other trusted browsers");
      reload();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusyId(null); }
  };

  return (
    <div className="space-y-4" data-testid="super-security-tab">
      <div className={`rounded-lg border p-3 ${ts && !ts.deliverable ? "border-amber-300/70 bg-amber-50/70 dark:bg-amber-950/20" : "bg-muted/40"}`} data-testid="super-security-two-step">
        <div className="flex items-start gap-2.5">
          {ts && !ts.deliverable ? <ShieldAlert className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" /> : <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium">Two-step sign-in (SMS code)</p>
              <Badge variant="outline" className="text-[10px]">Always required</Badge>
              {ts && (
                <Badge className={`text-[10px] ${ts.deliverable ? "bg-emerald-600 hover:bg-emerald-600" : "bg-amber-600 hover:bg-amber-600"}`} data-testid="super-security-two-step-status">
                  {ts.deliverable ? "Active" : "Not active"}
                </Badge>
              )}
            </div>
            {loading && !ts && <p className="text-xs text-muted-foreground mt-1">Checking…</p>}
            {ts?.deliverable && <p className="text-xs text-muted-foreground mt-1">Every sign-in asks for a 6-digit code sent to <span className="font-mono">{ts.mobile_hint}</span>, unless the browser is trusted below.</p>}
            {ts && !ts.deliverable && <p className="text-xs text-amber-800 dark:text-amber-300 mt-1" data-testid="super-security-two-step-reason">Codes cannot be delivered right now, so sign-in falls back to the password only. {ts.reason_text}</p>}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium flex items-center gap-1.5"><Laptop className="h-4 w-4" /> Trusted browsers <span className="text-xs text-muted-foreground font-normal">({devices.length})</span></p>
          {devices.length > (devices.some((d) => d.current) ? 1 : 0) && (
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={revokeOthers} disabled={busyId === "all"} data-testid="super-security-revoke-others">
              <LogOut className="h-3.5 w-3.5 mr-1" /> Sign out other browsers
            </Button>
          )}
        </div>
        {devices.length === 0 ? (
          <p className="text-xs text-muted-foreground border border-dashed rounded-lg px-3 py-3" data-testid="super-security-no-devices">
            No trusted browsers. Tick "Don't ask again on this browser for 30 days" at sign-in to add one.
          </p>
        ) : (
          <ul className="divide-y rounded-lg border" data-testid="super-security-devices">
            {devices.map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`super-security-device-${d.id}`}>
                <div className="min-w-0">
                  <div className="text-sm flex items-center gap-2">
                    <span className="truncate">{d.label || "Browser"}</span>
                    {d.current && <Badge variant="secondary" className="text-[10px]">This browser</Badge>}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Added {fmtWhen(d.created_at)} · last used {fmtWhen(d.last_used_at)} · expires {fmtWhen(d.expires_at)}{d.ip ? ` · ${d.ip}` : ""}</p>
                </div>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive hover:text-destructive shrink-0" onClick={() => revoke(d.id)} disabled={busyId === d.id}
                  data-testid={`super-security-revoke-${d.id}`}>Sign out</Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-lg border p-3">
        <p className="text-sm font-medium flex items-center gap-1.5 mb-1"><Bell className="h-4 w-4" /> Security alerts</p>
        <p className="text-xs text-muted-foreground">
          You are told by {[data?.alerts?.email && "email", data?.alerts?.sms && "SMS"].filter(Boolean).join(" and ") || "email / SMS (add a mobile number)"} whenever the sign-in email,
          password or mobile number changes, and on every sign-in from a browser that is not trusted.
        </p>
      </div>

      {(data?.events || []).length > 0 && (
        <div>
          <p className="text-sm font-medium mb-2">Recent security events</p>
          <ul className="space-y-1" data-testid="super-security-events">
            {data.events.slice(0, 6).map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 text-xs rounded-md bg-muted/40 px-2.5 py-1.5">
                <span className="truncate">{EVENT_LABEL[e.kind] || e.kind}{e.details?.browser ? ` · ${e.details.browser}` : ""}{e.details?.ip ? ` (${e.details.ip})` : ""}</span>
                <span className="text-muted-foreground shrink-0">{fmtWhen(e.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Platform Super Admin — "My account" dialog opened from the sidebar / header identity block.
 *   Profile tab : photo (pick → crop → save), name, mobile number, sign-in email (needs the current password)
 *   Password tab: change the sign-in password
 * `onSaved(user, token?)` patches the session so the header / sidebar update immediately.
 */
export function SuperProfileDialog({ open, onOpenChange, user, onSaved }) {
  const [profile, setProfile] = useState(user || null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  // fresh copy of the account whenever the dialog opens (the stored session may predate a change)
  useEffect(() => {
    if (!open) return;
    const fill = (u) => { setProfile(u); setName(u?.name || ""); setEmail(u?.email || ""); setMobile(u?.mobile || ""); };
    fill(user);
    setCurrentPw("");
    setPw({ current: "", next: "", confirm: "" });
    api.get("/platform/auth/me").then((r) => fill(r.data)).catch(() => {});
  }, [open, user]);

  const emailChanged = email.trim().toLowerCase() !== (profile?.email || "").toLowerCase();
  const dirty = name.trim() !== (profile?.name || "") || emailChanged || (mobile.trim() || "") !== (profile?.mobile || "");

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!dirty) return;
    if (emailChanged && !currentPw) return toast.error("Enter your current password to change the sign-in email");
    setBusy(true);
    try {
      const body = { name: name.trim(), email: email.trim(), mobile: mobile.trim() };
      if (emailChanged) body.current_password = currentPw;
      const r = await api.put("/platform/auth/me", body);
      setProfile(r.data.user);
      setCurrentPw("");
      onSaved?.(r.data.user, r.data.token);
      toast.success(emailChanged ? "Profile saved — use the new email the next time you sign in" : "Profile saved");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (!pw.current || !pw.next) return toast.error("Fill in the current and the new password");
    if (pw.next !== pw.confirm) return toast.error("New password and confirmation do not match");
    setPwBusy(true);
    try {
      const r = await api.post("/platform/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      setSuperDeviceToken(null); // every trusted browser (this one included) must verify an SMS code again
      toast.success("Password changed", { description: r.data?.trusted_devices_revoked ? `${r.data.trusted_devices_revoked} trusted browser${r.data.trusted_devices_revoked === 1 ? "" : "s"} signed out · alert sent` : "Alert sent to your email / mobile" });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="super-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">My account</DialogTitle>
          <DialogDescription>Your photo and name appear in the header and on platform activity logs. The email is your sign-in identity; the mobile number receives your sign-in codes.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="profile">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="profile" data-testid="super-profile-tab-profile"><UserRound className="h-3.5 w-3.5 mr-1.5" /> Profile</TabsTrigger>
            <TabsTrigger value="password" data-testid="super-profile-tab-password"><KeyRound className="h-3.5 w-3.5 mr-1.5" /> Password</TabsTrigger>
            <TabsTrigger value="security" data-testid="super-profile-tab-security"><ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Security</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-4">
            <AvatarEditor path="/platform/auth/me/photo" userId="me" name={profile?.name} photoUrl={profile?.photo_url || null}
              onChanged={(url) => { setProfile((p) => ({ ...(p || {}), photo_url: url })); onSaved?.({ photo_url: url }); }}
              testId="super-profile-photo" />
            <Separator className="my-4" />
            <form onSubmit={saveProfile} className="space-y-3" data-testid="super-profile-form">
              <div className="space-y-1.5">
                <Label htmlFor="sp-name">Full name</Label>
                <Input id="sp-name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} data-testid="super-profile-name-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="sp-mobile" className="flex items-center gap-1.5"><Smartphone className="h-3.5 w-3.5" /> Mobile number</Label>
                  <Input id="sp-mobile" inputMode="numeric" placeholder="10-digit mobile" value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="super-profile-mobile-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sp-email" className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Sign-in email</Label>
                  <Input id="sp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="super-profile-email-input" />
                </div>
              </div>
              {emailChanged && (
                <div className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="super-profile-email-confirm">
                  <Label htmlFor="sp-current-pw" className="text-amber-900">Current password (required to change the sign-in email)</Label>
                  <Input id="sp-current-pw" type="password" autoComplete="current-password" value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)} data-testid="super-profile-current-password-input" />
                  <p className="text-[11px] text-amber-800">Password-reset links and OTPs will go to the new address from now on.</p>
                </div>
              )}
              <div className="flex justify-end pt-1">
                <Button type="submit" disabled={!dirty || busy} data-testid="super-profile-save-button">
                  <Save className="h-4 w-4 mr-2" /> {busy ? "Saving..." : "Save changes"}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="password" className="mt-4">
            <form onSubmit={changePassword} className="space-y-3" data-testid="super-password-form">
              <div className="space-y-1.5">
                <Label htmlFor="sp-pw-current">Current password</Label>
                <Input id="sp-pw-current" type="password" autoComplete="current-password" value={pw.current}
                  onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} required data-testid="super-password-current-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sp-pw-next">New password</Label>
                <Input id="sp-pw-next" type="password" autoComplete="new-password" value={pw.next}
                  onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} required minLength={8} data-testid="super-password-new-input" />
                <p className="text-[11px] text-muted-foreground">At least 8 characters with 1 uppercase letter and 1 number.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sp-pw-confirm">Confirm new password</Label>
                <Input id="sp-pw-confirm" type="password" autoComplete="new-password" value={pw.confirm}
                  onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} required data-testid="super-password-confirm-input" />
              </div>
              <p className="text-[11px] text-muted-foreground">Changing the password signs out every trusted browser and sends you an alert by email and SMS.</p>
              <div className="flex justify-end pt-1">
                <Button type="submit" disabled={pwBusy} data-testid="super-password-save-button">
                  <KeyRound className="h-4 w-4 mr-2" /> {pwBusy ? "Changing..." : "Change password"}
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="security" className="mt-4">
            <SecurityTab open={open} profile={profile} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
